import React, { useEffect, useMemo, useState } from 'react';
import './spaces.css';
import SEO from '../SEO.js';

// The colored squares directly under the white text box. 7 columns x 3 rows.
const squares = [
    '#D31A1A', '#C8FF2B', '#4A5AED', '#FF00E5', '#FFC701', '#188773', '#B27AFA',
    '#56FF85', '#4800A4', '#E07648', '#1AF3F3', '#6D1419', '#D9D9D9', '#309D2E',
    '#9766D4', '#FF3D00', '#1B1157', '#E37575', '#998AAB', '#A66C15', '#586317',
];

const COLS = 7;
// The StarfieldAnimation cell in the right panel — column 1, row 0 of the
// panel's 3x5 grid. Desktop subpages keep the 30%-wide panel (columns at
// 70-80/80-90/90-100vw), so column 1 is x:80-90vw. On mobile the panel
// narrows to 13% and only one column stays visible (space.css hides the
// other two), spanning the full 87-100vw strip — matching that here keeps
// tiles emerging from the panel instead of empty space beside it.
const isMobilePanel = typeof window !== 'undefined' && window.innerWidth <= 767;
const SRC = isMobilePanel
    ? { x0: 87, x1: 100, y0: 0, y1: 20 }
    : { x0: 80, x1: 90, y0: 0, y1: 20 };
const START_SCALE = 0.15; // matches the animation squares (15% of the cell)

// Left edge of the right panel itself (not SRC.x0, which is only the
// animation cell within it — column 1 of 3 on desktop, so 10vw short of
// the panel edge). Matches #rightpanel's width in home.css: 30% on
// desktop (edge at 70vw), 13% on mobile (edge at 87vw).
const PANEL_EDGE = isMobilePanel ? 87 : 70;

// Tile column width, in vw. Sized so COLS columns exactly reach PANEL_EDGE
// — a fixed 10vw here matched desktop by coincidence but left a white gap
// before the (narrower) mobile panel.
const TILE_W = PANEL_EDGE / COLS;

// Three tiles are styled as doors and swing open once everything has landed.
// All three link out.
const LINK_INDEX = 10;
const LINK_URL = 'https://reclaiming-swart.vercel.app/';
const DOOR2_INDEX = 4;
const DOOR2_URL = 'https://inheritance-sigma.vercel.app/';
const DOOR3_INDEX = 15;
const DOOR3_URL = 'https://cadencelord.vercel.app/';

const DEFAULT_BLURB = 'each door explores a new creative work';
const MUSIC_BLURB = 'explore some of my music here';
const WHEELZ_BLURB = 'the act of name reclaiming';
const DOOR3_BLURB = 'visit a silly interactive piece';

const Spaces = () => {
    // Final grid slot (viewport coords) + the start offset back to the
    // right-panel source, scatter, spin and a stagger, per tile.
    const tiles = useMemo(
        () => squares.map((color, i) => {
            const c = i % COLS;
            const r = Math.floor(i / COLS);
            const left = c * TILE_W;             // vw
            const top = 40 + r * 20;             // dvh
            const cx = left + TILE_W / 2;         // tile centre x (vw)
            const cy = top + 10;                 // tile centre y (dvh)
            // a random spot inside the animation cell
            const sx = SRC.x0 + Math.random() * (SRC.x1 - SRC.x0);
            const sy = SRC.y0 + Math.random() * (SRC.y1 - SRC.y0);
            return {
                color, left, top,
                dx: (sx - cx).toFixed(1),
                dy: (sy - cy).toFixed(1),
                rot: (Math.random() * 40 - 20).toFixed(0),
                delay: (Math.random() * 0.5).toFixed(2),
                door: i === LINK_INDEX || i === DOOR2_INDEX || i === DOOR3_INDEX,
                link: i === LINK_INDEX ? LINK_URL
                    : i === DOOR2_INDEX ? DOOR2_URL
                    : i === DOOR3_INDEX ? DOOR3_URL
                    : null,
            };
        }),
        []
    );

    // Flip to the settled state on the next frame so the CSS transition runs.
    const [settled, setSettled] = useState(false);
    useEffect(() => {
        const id = requestAnimationFrame(() => requestAnimationFrame(() => setSettled(true)));
        return () => cancelAnimationFrame(id);
    }, []);

    // Every tile flies in over 1.6s with up to a 0.5s stagger delay, so the
    // last one lands by ~2.1s. Only then does the door swing open.
    const [landed, setLanded] = useState(false);
    useEffect(() => {
        const id = setTimeout(() => setLanded(true), 2300);
        return () => clearTimeout(id);
    }, []);

    // Hovering the yellow door (DOOR2_INDEX) fades a hint onto the tile beside it.
    const [musicHover, setMusicHover] = useState(false);
    // Hovering the blue/cyan door (LINK_INDEX) fades a hint onto the tile beside it.
    const [wheelzHover, setWheelzHover] = useState(false);
    // Hovering the orange door (DOOR3_INDEX) fades a hint onto the tile beside it.
    const [door3Hover, setDoor3Hover] = useState(false);

    return (
        <>
            <SEO
                title="Spaces"
                description="Spaces — a collection of creative experiments by Lord Crawford. Each door explores a new creative work."
                path="/spaces"
            />
            <div id="spaces_left" className="bg-white h-[100dvh] overflow-hidden">
                <div id="spaces_text">
                    <div className="text-[4vw] mb-[1.5vw] space-y-[-2vw] invisible title-spacer" aria-hidden="true"><div>Lord</div><div>Crawford</div></div>
                    <p
                        className={
                            "font-raleway font-light text-[3.4vw] md:text-[1.4vw] transition-opacity duration-700" +
                            (landed ? " opacity-100" : " opacity-0")
                        }
                    >
                        {DEFAULT_BLURB}
                    </p>
                </div>
                {/* spacer that keeps the lower 60dvh of the panel reserved */}
                <div id="spaces_squares" />
            </div>

            {/* Full-viewport overlay (above the right panel) so the tiles can be
                seen travelling out of the right-panel animation into the grid. */}
            <div id="spaces_flyer">
                {tiles.map((t, i) => {
                    const className =
                        'spaces_cell' +
                        (t.door ? ' door' : '') +
                        (t.door && landed ? ' door-open' : '');
                    const style = {
                        backgroundColor: t.color,
                        width: `${TILE_W}vw`,
                        left: `${t.left}vw`,
                        top: `${t.top}dvh`,
                        opacity: settled ? 1 : 0,
                        // While flying in, the inline transform drives the motion. Once
                        // settled it's cleared so CSS (.door-open, :hover) takes over —
                        // that's what lets the hover-open-further effect work at all.
                        transform: settled
                            ? undefined
                            : `translate(${t.dx}vw, ${t.dy}dvh) scale(${START_SCALE}) rotate(${t.rot}deg)`,
                        transitionDelay: `${t.delay}s`,
                    };
                    if (t.link) {
                        const isMusicDoor = i === DOOR2_INDEX;
                        const isWheelzDoor = i === LINK_INDEX;
                        const isDoor3 = i === DOOR3_INDEX;
                        return (
                            <a
                                key={i}
                                className={className}
                                style={style}
                                href={t.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Open project"
                                onMouseEnter={
                                    isMusicDoor ? () => setMusicHover(true)
                                    : isWheelzDoor ? () => setWheelzHover(true)
                                    : isDoor3 ? () => setDoor3Hover(true)
                                    : undefined
                                }
                                onMouseLeave={
                                    isMusicDoor ? () => setMusicHover(false)
                                    : isWheelzDoor ? () => setWheelzHover(false)
                                    : isDoor3 ? () => setDoor3Hover(false)
                                    : undefined
                                }
                            >
                                <span className="door_knob" />
                            </a>
                        );
                    }
                    if (t.door) {
                        return (
                            <div key={i} className={className} style={style} aria-hidden="true">
                                <span className="door_knob" />
                            </div>
                        );
                    }
                    if (i === DOOR2_INDEX + 1) {
                        return (
                            <div key={i} className={className} style={style}>
                                <span className={"spaces_hint" + (musicHover ? " spaces_hint_visible" : "")}>
                                    {MUSIC_BLURB}
                                </span>
                            </div>
                        );
                    }
                    if (i === LINK_INDEX + 1) {
                        return (
                            <div key={i} className={className} style={style}>
                                <span className={"spaces_hint" + (wheelzHover ? " spaces_hint_visible" : "")}>
                                    {WHEELZ_BLURB}
                                </span>
                            </div>
                        );
                    }
                    if (i === DOOR3_INDEX + 1) {
                        return (
                            <div key={i} className={className} style={style}>
                                <span className={"spaces_hint" + (door3Hover ? " spaces_hint_visible" : "")}>
                                    {DOOR3_BLURB}
                                </span>
                            </div>
                        );
                    }
                    return <div key={i} className={className} style={style} />;
                })}
            </div>
        </>
    );
};

export default Spaces;
